import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createIntegrationAPI,
  getIntegrationsAPI,
  getIntegrationByIdAPI,
  updateIntegrationAPI,
  deleteIntegrationAPI,
  getHotelIntegrationDetailsAPI,
} from "../../api/integrations";

//   Create Integration

export const createIntegration = createAsyncThunk(
  "integrations/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createIntegrationAPI(payload);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//   Fetch All Integrations

export const getIntegrations = createAsyncThunk(
  "integrations/getAll",
  async (hotelId = null, { rejectWithValue }) => {
    try {
      return await getIntegrationsAPI(hotelId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//  Fetch Single Integration

export const getIntegrationById = createAsyncThunk(
  "integrations/getById",
  async (id, { rejectWithValue }) => {
    try {
      return await getIntegrationByIdAPI(id);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//  Update Integration

export const updateIntegration = createAsyncThunk(
  "integrations/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await updateIntegrationAPI({ id, data });
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

//   Delete Integration

export const deleteIntegration = createAsyncThunk(
  "integrations/delete",
  async (id, { rejectWithValue }) => {
    try {
      return await deleteIntegrationAPI(id);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Fetch Integration Configuration Details

export const getHotelIntegrationDetails = createAsyncThunk(
  "integrations/getHotelIntegrationDetails",
  async ({ hotelId, providerKey }, { rejectWithValue }) => {
    try {
      return await getHotelIntegrationDetailsAPI({
        hotelId,
        providerKey,
      });
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const integrationsSlice = createSlice({
  name: "integrations",
  initialState: {
    integrations: [],
    selectedIntegration: null,

    // integration config details
    integrationDetails: null,
    integrationDetailsLoading: false,
    integrationDetailsError: null,

    // create
    createLoading: false,
    createSuccess: false,
    createError: null,

    // fetch all
    fetchLoading: false,
    fetchError: null,

    // fetch one
    fetchOneLoading: false,
    fetchOneError: null,

    // update
    updateLoading: false,
    updateSuccess: false,
    updateError: null,

    // delete
    deleteLoading: false,
    deleteSuccess: false,
    deleteError: null,
  },

  reducers: {
    resetCreateIntegrationState: (state) => {
      state.createLoading = false;
      state.createSuccess = false;
      state.createError = null;
    },

    resetUpdateIntegrationState: (state) => {
      state.updateLoading = false;
      state.updateSuccess = false;
      state.updateError = null;
    },

    resetDeleteIntegrationState: (state) => {
      state.deleteLoading = false;
      state.deleteSuccess = false;
      state.deleteError = null;
    },

    clearIntegrationDetails: (state) => {
      state.integrationDetails = null;
      state.integrationDetailsLoading = false;
      state.integrationDetailsError = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ───────── CREATE ───────── */
      .addCase(createIntegration.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.createError = null;
      })
      .addCase(createIntegration.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;

        const newProvider = action.payload.data.provider;
        state.integrations.unshift(newProvider);
      })
      .addCase(createIntegration.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
      })

      /* ───────── GET ALL ───────── */
      .addCase(getIntegrations.pending, (state) => {
        state.fetchLoading = true;
        state.fetchError = null;
      })
      .addCase(getIntegrations.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = null;

        const { providers = [], statusByProvider = [] } = action.payload?.data || {};

        state.integrations = providers.map((provider) => {
          const status = statusByProvider.find((s) => s.providerKey === provider.key);

          return {
            ...provider,
            connected: status?.connected || false,
            integrationDetails: status?.integration || null,
          };
        });
      })
      // .addCase(getIntegrations.fulfilled, (state, action) => {
      //   state.fetchLoading = false;
      //   state.fetchError = null;
      //   const { data } = action.payload;
      //   console.log("data in slice  ===>", data.statusByProvider);
      //   state.integrations = data.providers;
      // })
      .addCase(getIntegrations.rejected, (state, action) => {
        state.fetchLoading = false;
        state.fetchError = action.payload;
      })

      /* ───────── GET ONE ───────── */
      .addCase(getIntegrationById.pending, (state) => {
        state.fetchOneLoading = true;
        state.fetchOneError = null;
      })
      .addCase(getIntegrationById.fulfilled, (state, action) => {
        state.fetchOneLoading = false;
        state.selectedIntegration = action.payload;
      })
      .addCase(getIntegrationById.rejected, (state, action) => {
        state.fetchOneLoading = false;
        state.fetchOneError = action.payload;
      })

      /* ───────── UPDATE ───────── */
      .addCase(updateIntegration.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.updateError = null;
      })
      .addCase(updateIntegration.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;

        const updatedProvider = action.payload.data.provider;
        state.integrations = state.integrations.map((item) =>
          item._id === updatedProvider._id ? updatedProvider : item
        );
        state.selectedIntegration = action.payload;
      })
      .addCase(updateIntegration.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })

      /* ───────── DELETE ───────── */
      .addCase(deleteIntegration.pending, (state) => {
        state.deleteLoading = true;
        state.deleteSuccess = false;
        state.deleteError = null;
      })
      .addCase(deleteIntegration.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.integrations = state.integrations.filter((item) => item._id !== action.meta.arg);
      })
      .addCase(deleteIntegration.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
      })

      /* ───────── GET INTEGRATION DETAILS ───────── */
      .addCase(getHotelIntegrationDetails.pending, (state) => {
        state.integrationDetailsLoading = true;
        state.integrationDetailsError = null;
      })
      .addCase(getHotelIntegrationDetails.fulfilled, (state, action) => {
        state.integrationDetailsLoading = false;
        state.integrationDetails = action.payload?.data?.integration || null;
      })
      .addCase(getHotelIntegrationDetails.rejected, (state, action) => {
        state.integrationDetailsLoading = false;
        state.integrationDetailsError = action.payload;
      });
  },
});

export const {
  clearIntegrationsState,
  resetCreateIntegrationState,
  resetUpdateIntegrationState,
  clearSelectedIntegration,
  clearIntegrationDetails,
} = integrationsSlice.actions;

export default integrationsSlice.reducer;
